import styled, { css } from 'styled-components'

/** Design token: --text-strong / --nocfo-menu-bg */
const textStrong = ({ theme }: { theme: { colors: { menuBg: string } } }) => theme.colors.menuBg
/** Design token: --fg-4 / icon-gray */
const fg4 = ({ theme }: { theme: { colors: { iconGray: string } } }) => theme.colors.iconGray
/** Design token: --border-default / soft border (~#E4E8EC) — nearest theme token is neutralGray;
 *  use a literal for the slightly cooler border-default from the design system. */
const borderDefault = '#E4E8EC'
const borderSubtle = ({ theme }: { theme: { colors: { neutralGray: string } } }) =>
  theme.colors.neutralGray

/** Narrow detail panel: side panel on desktop or mobile viewport */
const narrowDetailPanel = (styles: ReturnType<typeof css>) => css`
  @container invoice-detail (max-width: 36rem) {
    ${styles}
  }

  @media (max-width: 48em) {
    ${styles}
  }
`

export const StyledProductDetails = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  display: flex;
  flex-direction: column;
  container-type: inline-size;
  container-name: invoice-detail;
`

export const DetailSection = styled.section`
  margin-bottom: 1.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`

export const DetailMetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.75rem;
  row-gap: 1.375rem;
  margin-bottom: 0;

  ${narrowDetailPanel(css`
    grid-template-columns: 1fr;
    row-gap: 1rem;
  `)}
`

export const DetailMetaField = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${({ $fullWidth }) => ($fullWidth ? '1 / -1' : 'auto')};
  min-width: 0;
`

export const StyledHeader = styled.p`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  color: ${fg4};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.25rem;

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
    flex-shrink: 0;
  }
`

export const StyledValue = styled.div<{ $emphasize?: 'warning' }>`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: ${({ $emphasize }) => ($emphasize ? 600 : 400)};
  color: ${({ theme, $emphasize }) =>
    $emphasize === 'warning' ? theme.colors.warning : theme.colors.metalGray};
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: center;
  min-width: 0;

  a {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 1rem 1.25rem 1.25rem;

  ${narrowDetailPanel(css`
    padding: 0.85rem 0.85rem 1rem;
  `)}
`

export const ContentFooter = styled.div<{ $spread?: boolean }>`
  display: flex;
  align-items: center;
  /* flex-end: space-between misaligns lone wrapped items; status uses margin-right: auto. */
  justify-content: flex-end;
  flex-wrap: wrap;
  padding: 1rem 1.25rem;
  gap: ${({ theme }) => theme.spacing.md}rem;
  background: #fafbfc;
  border-top: 1px solid ${borderDefault};

  ${narrowDetailPanel(css`
    padding: 0.85rem;
  `)}

  ${({ $spread }) =>
    !$spread &&
    narrowDetailPanel(css`
      justify-content: stretch;

      > * {
        flex: 1 1 auto;
        min-width: 0;
      }
    `)}
`

export const ContentFooterStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  margin-right: auto;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${fg4};

  svg {
    width: 0.95rem;
    height: 0.95rem;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }

  strong {
    color: ${textStrong};
    font-weight: 600;
  }
`

export const StyledValueContainer = styled.div<{ $emphasize?: 'warning' }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: ${({ $emphasize }) => ($emphasize ? 600 : 400)};
  color: ${({ theme, $emphasize }) =>
    $emphasize === 'warning' ? theme.colors.warning : theme.colors.metalGray};

  & > span {
    margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
  }

  a {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const StatusBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: center;
`

export const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
  color: ${fg4};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.4;

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
    flex-shrink: 0;
  }
`

export const HeaderMetaStrong = styled.strong`
  color: ${({ theme }) => theme.colors.metalGray};
  font-weight: 600;
`

export const InvoiceLineItemsTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const InvoiceLineItemHeader = styled.div`
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr) auto;
  gap: 4px 12px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  color: ${fg4};
  padding-bottom: 8px;
  border-bottom: 1px solid ${borderDefault};

  ${narrowDetailPanel(css`
    display: none;
  `)}
`

export const InvoiceLineItemRow = styled.div`
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr) auto;
  grid-template-areas: 'qty desc amount';
  gap: 4px 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${borderSubtle};
  align-items: start;

  ${narrowDetailPanel(css`
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'desc amount'
      'meta amount';
    gap: 2px 12px;
    padding: 0.85rem 0;
  `)}
`

export const InvoiceLineItemQuantity = styled.div`
  grid-area: qty;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 400;
  color: ${fg4};
  white-space: nowrap;
  padding-top: 1px;

  ${narrowDetailPanel(css`
    display: none;
  `)}
`

export const InvoiceLineItemDescription = styled.div`
  grid-area: desc;
  min-width: 0;
`

export const InvoiceLineItemName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 400;
  color: ${textStrong};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const InvoiceLineItemNameMeta = styled.div`
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  color: ${fg4};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${narrowDetailPanel(css`
    display: none;
  `)}
`

export const InvoiceLineItemMobileMeta = styled.div`
  display: none;
  grid-area: meta;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  color: ${fg4};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${narrowDetailPanel(css`
    display: block;
  `)}
`

export const InvoiceLineItemAmount = styled.div`
  grid-area: amount;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${textStrong};
  font-variant-numeric: tabular-nums;
  padding-top: 1px;

  & > *:first-child {
    font-size: inherit;
    font-weight: 600;
  }
`

export const InvoiceLineItemAmountMeta = styled.div`
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  color: ${fg4};
  white-space: nowrap;

  & > * {
    font-size: inherit;
    font-weight: inherit;
    color: inherit;
  }

  ${narrowDetailPanel(css`
    display: none;
  `)}
`

export const InvoiceLineItemTotals = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.md}rem;

  ${narrowDetailPanel(css`
    justify-content: stretch;
  `)}
`

export const InvoiceLineItemTotalsInner = styled.div`
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.defaultHover};

  ${narrowDetailPanel(css`
    min-width: 0;
    width: 100%;
  `)}
`

export const InvoiceLineItemTotal = styled.div<{ $emphasis?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;

  ${({ $emphasis, theme }) =>
    $emphasis &&
    `
      margin-top: ${theme.spacing.xxs}rem;
      padding-top: ${theme.spacing.sm}rem;
      border-top: 1px solid ${borderDefault};
    `}
`

export const InvoiceLineItemTotalLabel = styled.span<{ $emphasis?: boolean }>`
  color: ${({ theme, $emphasis }) => ($emphasis ? theme.colors.menuBg : theme.colors.iconGray)};
  font-weight: ${({ $emphasis }) => ($emphasis ? 700 : 400)};
`

export const InvoiceLineItemTotalValue = styled.span<{ $emphasis?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: ${({ $emphasis }) => ($emphasis ? 700 : 400)};
  color: ${({ theme, $emphasis }) => ($emphasis ? theme.colors.menuBg : theme.colors.metalGray)};
  font-variant-numeric: tabular-nums;
  text-align: right;

  span {
    font-size: 1em !important;
    font-weight: inherit !important;
    color: inherit !important;
    line-height: inherit;
  }
`

export const AttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

export const SectionLabel = styled.div`
  color: ${fg4};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
`
